/* AXIS foil fit tool, UI layer (ported from Live2Kite, 2026-09-25).
 *
 * The rules and the verdict logic are NOT here: they live in foil-fit-engine.js, which
 * is scripts/lib/fit.js uploaded unchanged, reading foil-fit-rules.json, which is
 * public/data/compatibility-rules.json uploaded unchanged. One rule set serves the
 * advisor, this tool and anything built later; that is deliberate, because the four
 * incompatible product-code systems in this business all started as a second copy.
 *
 * The catalogue comes from foil-fit-data.json, generated from the spec.* metafields, so
 * the tool and the storefront filters read the same values. Parts the shipping sheet
 * reports sold out are already absent from it.
 *
 * SHAPED BY EVAN'S CORRECTIONS, 2026-09-19:
 *
 *  - A MAST DOES NOT RELATE TO A FRONT WING. It relates to the FUSELAGE. The first
 *    version judged masts against the wing by silently picking the first fuselage that
 *    fitted, which is a guess dressed as an answer. The fuselage is now an explicit
 *    choice the rider can see and change, and the mast and rear answers say which
 *    fuselage they are for.
 *  - "What fits" and "what does not fit", each as a pull-down per category, rather than
 *    a count of how many do and do not.
 *  - Families newest first, never alphabetical: Surge, Tempo, Fireball, PNG V2 and so
 *    on, from familyOrder in the rules.
 *  - Rider weight now earns its place. It used to drive only two MAST edge cases, which
 *    is exactly the relationship Evan decoupled, so it drove nothing that mattered. It
 *    now answers what size rear at your weight, from guidance.rearSizeByWeightKg.
 *
 * Everything degrades: if an asset fails to load the mount point says so plainly rather
 * than rendering an empty widget.
 */
(function () {
  'use strict';

  var SEV = {
    ok: { label: 'Fits', cls: 'fit-ok' },
    info: { label: 'Fits, with a note', cls: 'fit-info' },
    warn: { label: 'Not recommended', cls: 'fit-warn' },
    block: { label: 'Does not fit', cls: 'fit-block' }
  };
  var ORDER = { ok: 0, info: 1, warn: 2, block: 3 };
  var CATS = [
    { key: 'fuselage', label: 'Fuselages' },
    { key: 'mast', label: 'Masts' },
    { key: 'rear wing', label: 'Rear wings' }
  ];

  function el(tag, cls, text) {
    var e = document.createElement(tag);
    if (cls) e.className = cls;
    if (text != null) e.textContent = text;
    return e;
  }
  function label(p) {
    return p.title.replace(/^AXIS Foils\s+/i, '').replace(/^AXIS\s+/i, '');
  }
  function group(parts, cat) {
    return parts.filter(function (p) { return p.category === cat; });
  }

  /**
   * The family to ORDER by, which is not always the family on the product.
   *
   * familyOrder is written in the RULES' vocabulary and the metafield carries the
   * CLASSIFIER'S, and for fuselages they are different by design: the classifier says
   * "Black Advance+ Crazyshort" (family plus length) where the rules say "Black
   * Advance+". Measured: only 1 of 26 fuselages matched familyOrder on the metafield, so
   * fuselage ordering fell through entirely and the default fuselage was whatever sorted
   * first by accident. Resolve through the engine and fall back to the metafield.
   */
  function orderFamily(rules, fit, cat, p) {
    var f = null;
    if (cat === 'fuselage') f = fit.fuselageFamily(rules, p.title);
    else if (cat === 'rear wing') f = fit.rearFamily(rules, p.title);
    else if (cat === 'mast') f = fit.mastFamily(rules, p.title);
    return (f && f.family) || p.family || null;
  }

  /**
   * The sub category a mast belongs to: Carbon, Aluminium, Integrated - Foil Drive
   * (Evan, 2026-09-20). Defined in the RULES, not here, so the page, the setup builder
   * and anything built on Dawn read one list. An unmapped family falls into "Other" and
   * sorts last rather than vanishing.
   */
  function mastGroup(rules, fam, title) {
    var g = rules.mastGroups;
    if (!g) return null;
    // A DISPLAY group is not a FIT family. The FATTY Integrated E-Foil mast stays in the
    // Power Carbon Fatty family, because Fatty decides which fuselage it bolts to, but a
    // customer browsing masts looks for it under Integrated.
    var ov = g.titleOverrides || [];
    for (var j = 0; j < ov.length; j++) {
      if (title && new RegExp(ov[j].match, 'i').test(title)) return ov[j].group;
    }
    if (!fam) return null;
    var names = g.order || [];
    for (var i = 0; i < names.length; i++) {
      if ((g.families[names[i]] || []).indexOf(fam) !== -1) return names[i];
    }
    return 'Other';
  }
  function mastGroupRank(rules, fam, title) {
    var g = rules.mastGroups;
    if (!g) return 0;
    var i = (g.order || []).indexOf(mastGroup(rules, fam, title));
    return i === -1 ? (g.order || []).length : i;
  }


  /** Pricelist position, for fuselages (Evan, 2026-09-20). Listed parts first, in list order. */
  function plCompare(a, b) {
    var ra = a.plRank, rb = b.plRank;
    var ha = ra !== null && ra !== undefined, hb = rb !== null && rb !== undefined;
    if (ha && hb) return ra - rb;
    if (ha !== hb) return ha ? -1 : 1;
    return 0;
  }

  /** Newest family first, then larger sizes first. Never alphabetical (Evan). */
  function sorter(rules, fit, cat) {
    var order = (rules.familyOrder && rules.familyOrder[cat]) || [];
    var rank = {};
    order.forEach(function (f, i) { rank[f.toLowerCase()] = i; });
    return function (a, b) {
      if (cat === 'fuselage') { var pl = plCompare(a, b); if (pl) return pl; }
      var fa = orderFamily(rules, fit, cat, a);
      var fb = orderFamily(rules, fit, cat, b);
      if (cat === 'mast') {
        var ga = mastGroupRank(rules, fa, a.title), gb = mastGroupRank(rules, fb, b.title);
        if (ga !== gb) return ga - gb;
      }
      var ra = rank[String(fa || '').toLowerCase()];
      var rb = rank[String(fb || '').toLowerCase()];
      // A family the order does not name sorts after every family it does, rather than
      // silently landing at the top as an undefined would.
      if (ra === undefined) ra = order.length + (fa ? 0 : 1);
      if (rb === undefined) rb = order.length + (fb ? 0 : 1);
      if (ra !== rb) return ra - rb;
      // span/chord, NOT size: size is a string and reads "300/45" on rears, so
      // Number(size) is NaN and the comparison was meaningless.
      var sa = Number(a.span) || 0, sb = Number(b.span) || 0;
      // MASTS RUN SHORTEST FIRST (Evan, 2026-09-20: "the 105 should go after the 90").
      // Wings and rears stay largest first; a mast is chosen by how deep you ride, and
      // the range reads naturally from short to long.
      if (sa !== sb) return cat === 'mast' ? sa - sb : sb - sa;
      var ca = Number(a.chord) || 0, cb = Number(b.chord) || 0;
      if (ca !== cb) return cb - ca;
      return label(a).localeCompare(label(b));
    };
  }

  function init(root, data, rules, fit) {
    var parts = data.parts;
    var fronts = group(parts, 'front wing');
    var state = { front: null, fuse: null, rider: null };

    // ---- the picker
    var pick = el('div', 'fit-pick');
    var sel = el('select', 'fit-select');
    sel.setAttribute('aria-label', 'Choose your front wing');
    sel.appendChild(new Option('Choose your front wing...', ''));
    var byFam = {};
    fronts.forEach(function (p) { (byFam[p.family || 'Other'] = byFam[p.family || 'Other'] || []).push(p); });
    var famOrder = Object.keys(byFam).sort(function (a, b) {
      return sorter(rules, fit, 'front wing')({ family: a, title: '', size: 0 }, { family: b, title: '', size: 0 });
    });
    famOrder.forEach(function (fam) {
      var g = document.createElement('optgroup');
      g.label = fam;
      byFam[fam].sort(sorter(rules, fit, 'front wing'))
        .forEach(function (p) { g.appendChild(new Option(label(p), p.handle)); });
      sel.appendChild(g);
    });
    var rider = el('input', 'fit-rider');
    rider.type = 'number';
    rider.min = '30'; rider.max = '150'; rider.placeholder = 'Your weight in kg (optional)';
    rider.setAttribute('aria-label', 'Rider weight in kilograms');

    pick.appendChild(sel);
    pick.appendChild(rider);
    root.appendChild(pick);
    // Evan, 2026-09-28: "still don't know the reason why we are asking for the weight". It feeds two checks only.
    root.appendChild(el('p', 'fit-rider-why', 'Your weight is only used to check the Kaiwi mast\u2019s rider limit and when a heavier rider needs the FATTY mast.'));
    var out = el('div', 'fit-out');
    root.appendChild(out);

    /** Judge one part, given the wing and the chosen fuselage. */
    function judge(cat, p) {
      if (cat === 'fuselage') return fit.fuselageFits(rules, state.front, p);
      if (!state.fuse) return null;
      if (cat === 'mast') return fit.mastFits(rules, state.fuse, p, state.front, state.rider);
      return fit.rearFits(rules, state.fuse, p);
    }

    /** The rear size AXIS suggests at this rider's weight, which is guidance, not fit. */
    function rearGuidance() {
      var g = rules.guidance && rules.guidance.rearSizeByWeightKg;
      if (!g || !state.rider) return null;
      for (var i = 0; i < g.length; i++) if (state.rider <= g[i].maxKg) return g[i];
      return null;
    }

    function render() {
      out.innerHTML = '';
      if (!state.front) {
        out.appendChild(el('p', 'fit-hint', 'Pick your front wing and we will show the fuselages, masts and rear wings that work with it, and say why when something does not.'));
        return;
      }
      var f = state.front;
      // The dropdown already shows the wing, so do not echo it as a heading and do not
      // repeat what the title itself says (Evan, 2026-09-19: the name appeared three
      // times in the top three lines). Show only the facts the title does NOT carry.
      var t = f.title.toLowerCase();
      var extra = [];
      // Evan, 2026-09-20: there is no Black "system", it is a Black FUSELAGE; and Ultra
      // High Modulus is a CONSTRUCTION, so say so.
      if (f.system) extra.push(f.system + ' Fuselage');
      if (f.construction && t.indexOf(f.construction.toLowerCase()) === -1) extra.push(f.construction + ' construction');
      if (f.size && t.indexOf(String(f.size).toLowerCase()) === -1) extra.push(f.size + ' mm');
      if (extra.length) out.appendChild(el('p', 'fit-meta', extra.join('  ·  ')));

      // An unidentified wing has no system, so every part below would render "does not
      // fit" for want of knowledge rather than for a real reason. Twenty-eight false
      // rejections is worse than saying plainly that we do not know this one.
      if (!f.system) {
        var note = el('div', 'fit-note');
        note.appendChild(el('p', null, 'This is an older wing and we do not have it on the fit table yet, so we will not guess at what it takes.'));
        note.appendChild(el('p', null, 'Get in touch through axisfoils.com/pages/contact-us and we will work out the fuselage, mast and rear with you.'));
        out.appendChild(note);
        return;
      }

      // ---- the fuselage drives the masts and the rears, so it is chosen, not assumed
      var fuseOk = group(parts, 'fuselage')
        .filter(function (x) { return fit.fuselageFits(rules, f, x).ok; })
        .sort(sorter(rules, fit, 'fuselage'));
      // THE TOOL DOES NOT CHOOSE A FUSELAGE FOR THE RIDER (Evan, 2026-09-20). It used to
      // preselect a Standard length, which reads as an answer rather than a question and
      // quietly decided what the mast and rear lists were about.
      if (state.fuse && fuseOk.indexOf(state.fuse) === -1) state.fuse = null;

      if (fuseOk.length) {
        var fw = el('div', 'fit-fuse-pick');
        fw.appendChild(el('label', 'fit-fuse-label', 'Fuselage'));
        var fs = el('select', 'fit-select');
        fs.setAttribute('aria-label', 'Choose your fuselage');
        fs.appendChild(new Option('Pick your fuselage...', ''));
        fuseOk.forEach(function (x) { fs.appendChild(new Option(label(x), x.handle)); });
        fs.value = state.fuse ? state.fuse.handle : '';
        fs.addEventListener('change', function () {
          state.fuse = fuseOk.filter(function (x) { return x.handle === fs.value; })[0] || null;
          render();
        });
        fw.appendChild(fs);
        fw.appendChild(el('p', 'fit-hint', state.fuse
          ? 'Masts and rear wings below fit this fuselage.'
          : 'Pick a fuselage to see which masts and rear wings work with it.'));
        out.appendChild(fw);
      }

      var fits = el('section', 'fit-yes');
      fits.appendChild(el('h3', null, 'What fits'));
      var nots = el('section', 'fit-no');
      nots.appendChild(el('h3', null, 'What does not fit'));

      CATS.forEach(function (c) {
        var list = group(parts, c.key).sort(sorter(rules, fit, c.key));
        var rows = [];
        list.forEach(function (p) {
          var v = judge(c.key, p);
          if (v) rows.push({ p: p, v: v });
        });
        // judge() returns null for masts and rears until a fuselage is chosen, which is
        // the honest state: both hang off the fuselage, so there is nothing to say yet.
        if (!rows.length && c.key !== 'fuselage' && !state.fuse) {
          var waiting = el('details', 'fit-drawer');
          var ws = el('summary', 'fit-drawer-head');
          ws.appendChild(el('span', 'fit-drawer-name', c.label));
          ws.appendChild(el('span', 'fit-count', '?'));
          waiting.appendChild(ws);
          waiting.appendChild(el('p', 'fit-hint', 'Pick your fuselage first.'));
          fits.appendChild(waiting);
          return;
        }
        var good = rows.filter(function (r) { return r.v.severity !== 'block'; });
        var bad = rows.filter(function (r) { return r.v.severity === 'block'; });
        fits.appendChild(drawer(c, good, c.key === 'rear wing'));
        nots.appendChild(drawer(c, bad, false));
      });

      out.appendChild(fits);
      out.appendChild(nots);
    }

    /** One pull-down: a category heading with its count, opening onto the list. */
    function drawer(c, rows, withGuidance) {
      var d = el('details', 'fit-drawer');
      var s = el('summary', 'fit-drawer-head');
      s.appendChild(el('span', 'fit-drawer-name', c.label));
      s.appendChild(el('span', 'fit-count', String(rows.length)));
      d.appendChild(s);
      if (!rows.length) {
        d.appendChild(el('p', 'fit-hint', 'Nothing in this category.'));
        return d;
      }
      if (withGuidance) {
        var g = rearGuidance();
        if (g) {
          var hint = 'At ' + state.rider + ' kg AXIS suggests a Skinny ' + g.skinny
            + ', Surf Skinny ' + g.surfSkinny + ', Progressive ' + g.progressive
            + ' or Freeride ' + g.freeride + '. That is ride guidance, not a fit rule: every rear listed here bolts on.';
          d.appendChild(el('p', 'fit-guidance', hint));
        }
      }
      var ul = el('ul', 'fit-list');
      // Group the list by family so the newest family reads as a block, which is how the
      // range is actually shopped.
      var lastFam = null;
      var lastGroup = null;
      rows.forEach(function (r) {
        var fam = orderFamily(rules, fit, c.key, r.p) || 'Other';
        if (c.key === 'mast') {
          // Masts head by SUB CATEGORY (Carbon / Aluminium / Integrated - Foil Drive) and
          // carry the family as a lighter second line, so "Pro Ultra High Modulus" is
          // still visible without three headings competing.
          var g = mastGroup(rules, fam, r.p.title) || 'Other';
          if (g !== lastGroup) { ul.appendChild(el('li', 'fit-group', g)); lastGroup = g; lastFam = null; }
        }
        // Displayed in FATTY capitals; `fam` itself stays the lookup key.
        if (fam !== lastFam) { ul.appendChild(el('li', 'fit-fam', String(fam).replace(/\bFatty\b/g, 'FATTY'))); lastFam = fam; }
        ul.appendChild(row(r));
      });
      d.appendChild(ul);
      return d;
    }

    function row(r) {
      var li = el('li', 'fit-row ' + SEV[r.v.severity].cls);
      var a = el('a', 'fit-name', label(r.p));
      a.href = '/products/' + r.p.handle;
      li.appendChild(a);
      // no "on order" label on axisfoils.com (Evan, 2026-09-29: "Dont show on order")
      if (r.v.severity !== 'ok') li.appendChild(el('span', 'fit-verdict', SEV[r.v.severity].label));
      if (r.v.reasons && r.v.reasons.length) li.appendChild(el('span', 'fit-why', r.v.reasons[0]));
      if (r.p.price) {
        // Same rule as the builder: original crossed out beside the price, only on a real
        // markdown, so the two tools can never disagree about whether something is on sale.
        var pr = el('span', 'fit-price');
        if (r.p.compareAt && Number(r.p.compareAt) > Number(r.p.price)) {
          pr.appendChild(el('s', 'fit-was', '$' + r.p.compareAt));
          pr.appendChild(document.createTextNode(' '));
          pr.appendChild(el('span', 'fit-now', '$' + r.p.price));
        } else {
          pr.appendChild(document.createTextNode('$' + r.p.price));
        }
        li.appendChild(pr);
      }
      return li;
    }

    sel.addEventListener('change', function () {
      state.front = fronts.filter(function (p) { return p.handle === sel.value; })[0] || null;
      state.fuse = null;
      render();
    });
    rider.addEventListener('change', function () {
      var n = Number(rider.value);
      state.rider = n > 0 ? n : null;
      render();
    });
    render();
  }

  function boot() {
    var root = document.getElementById('FoilFit');
    if (!root) return;
    var dataUrl = root.getAttribute('data-parts');
    var rulesUrl = root.getAttribute('data-rules');
    Promise.all([fetch(dataUrl).then(function (r) { return r.json(); }), fetch(rulesUrl).then(function (r) { return r.json(); })])
      .then(function (res) {
        if (!window.AxisFit) throw new Error('fit engine did not load');
        root.innerHTML = '';
        init(root, res[0], res[1], window.AxisFit);
      })
      .catch(function (e) {
        root.innerHTML = '';
        root.appendChild(el('p', 'fit-hint', 'The fit tool could not load just now. Get in touch through axisfoils.com/pages/contact-us and we will work your setup out with you.'));
        if (window.console) console.error('foil fit:', e);
      });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
